import React from "react";
import * as Survey from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";
import {
  DateInputParts,
  formatDateForDisplay,
  hasCompleteDateParts,
  isDateWithinRange,
  isValidDateParts,
  normalizeDateBoundary,
  parseDateParts,
  toIsoDateString,
} from "../../../helpers/DateHelpers";

const CUSTOM_TYPE = "datepickermultiinput";

interface DatePickerMultiInput extends DateInputParts {}

type DatePartKey = keyof DateInputParts;

const PART_MAX_LENGTH: Record<DatePartKey, number> = {
  day: 2,
  month: 2,
  year: 4,
};

function hasStorableDateParts(parts: DateInputParts): boolean {
  return (
    parts.day.length === 2 &&
    parts.month.length === 2 &&
    parts.year.length === 4
  );
}

export class QuestionDatePickerTextModel extends Survey.Question {
  public inputParts: DateInputParts | null = null;

  getType() {
    return CUSTOM_TYPE;
  }

  get minDate() {
    return this.getPropertyValue("min");
  }
  set minDate(val: string | Date) {
    this.setPropertyValue("min", normalizeDateBoundary(val));
  }

  get maxDate() {
    return this.getPropertyValue("max");
  }
  set maxDate(val: string | Date) {
    this.setPropertyValue("max", normalizeDateBoundary(val));
  }

  get readOnly() {
    return (
      this.getPropertyValue("readOnly") || this.getPropertyValue("readonly")
    );
  }
  set readOnly(val: boolean) {
    this.setPropertyValue("readOnly", val);
  }

  validate(): boolean {
    this.clearErrors();

    const value = this.value as string | undefined;
    const currentInputParts = this.inputParts;
    const hasAnyTypedValue = Boolean(
      currentInputParts?.day || currentInputParts?.month || currentInputParts?.year
    );

    if (
      !this.isRequired &&
      !hasAnyTypedValue &&
      (value === "" || value == null)
    ) {
      return true;
    }

    if (
      this.isRequired &&
      !hasAnyTypedValue &&
      (value === "" || value == null)
    ) {
      this.errors.push(new Survey.CustomError("This is required"));
      return false;
    }

    const parts = currentInputParts && hasAnyTypedValue ? currentInputParts : parseDateParts(value);

    if (!parts || !isValidDateParts(parts)) {
      this.errors.push(new Survey.CustomError("Please enter a valid date"));
      return false;
    }

    if (!isDateWithinRange(parts, this.min, this.max)) {
      this.errors.push(
        new Survey.CustomError(
          `Date must be between ${formatDateForDisplay(this.min)} and ${formatDateForDisplay(
            this.max
          )}`
        )
      );
      return false;
    }

    return true;
  }
}

Survey.Serializer.addClass(
  CUSTOM_TYPE,
  [{ name: "min:string|Date" }, { name: "max:string|Date" }],
  () => new QuestionDatePickerTextModel(""),
  "question"
);

Survey.ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
  return new QuestionDatePickerTextModel(name);
});

export class SurveyQuestionDatePickerText extends SurveyQuestionElementBase {
  private dayInputRef = React.createRef<HTMLInputElement>();
  private monthInputRef = React.createRef<HTMLInputElement>();
  private yearInputRef = React.createRef<HTMLInputElement>();

  // Prevents syncStateFromValue from firing during our own question.value writes.
  private _updating = false;

  constructor(props: any) {
    super(props);
    this.state = {
      day: "",
      month: "",
      year: "",
    };
  }

  get question() {
    return this.questionBase as QuestionDatePickerTextModel;
  }

  componentDidMount() {
    super.componentDidMount?.();
    this.syncStateFromValue();
    this.question.registerFunctionOnPropertyValueChanged(
      "value",
      this.handleQuestionValueChanged,
      "datepickertext-sync"
    );
  }

  componentDidUpdate(prevProps: any) {
    // Only sync when an external party sets a real (non-empty) date value.
    // Partial user entry sets question.value = "" — we must not overwrite state in that case.
    if (prevProps.question.value !== this.question.value && this.question.value) {
      this.question.inputParts = null;
      this.syncStateFromValue();
    }
  }

  componentWillUnmount() {
    this.question.unRegisterFunctionOnPropertyValueChanged(
      "value",
      "datepickertext-sync"
    );
    super.componentWillUnmount?.();
  }

  private handleQuestionValueChanged = () => {
    // Skip during our own writes — state already reflects the user's input.
    if (this._updating) return;
    this.question.inputParts = null;
    this.syncStateFromValue();
  };

  private syncStateFromValue() {
    const parts = parseDateParts(this.question.value);
    this.question.inputParts = parts;
    this.setState({
      day:   parts?.day   ?? "",
      month: parts?.month ?? "",
      year:  parts?.year  ?? "",
    } as DatePickerMultiInput);
  }

  private getInputRef(part: DatePartKey) {
    if (part === "day") return this.dayInputRef;
    if (part === "month") return this.monthInputRef;
    return this.yearInputRef;
  }

  private getNextPart(part: DatePartKey): DatePartKey | null {
    if (part === "day") return "month";
    if (part === "month") return "year";
    return null;
  }

  private focusPart(part: DatePartKey | null) {
    if (!part) return;
    this.getInputRef(part).current?.focus();
  }

  private updateQuestionValue(nextParts: DateInputParts) {
    this.question.inputParts = nextParts;
    this._updating = true;

    if (!nextParts.day && !nextParts.month && !nextParts.year) {
      this.question.value = "";
    } else if (hasCompleteDateParts(nextParts) && hasStorableDateParts(nextParts)) {
      this.question.value = toIsoDateString(nextParts);
    } else {
      this.question.value = "";
    }

    this._updating = false;
  }

  private normalizePartsOnBlur(part: DatePartKey) {
    const currentState = this.state as DatePickerMultiInput;

    if (currentState[part].length !== 1) {
      if (currentState.year.length === 4) this.question.validate();
      return;
    }

    const nextState = {
      ...currentState,
      [part]: currentState[part].padStart(2, "0"),
    } as DatePickerMultiInput;

    this.setState(nextState, () => {
      this.updateQuestionValue(nextState);
      if (nextState.year.length === 4) this.question.validate();
    });
  }

  private handlePartChange =
    (part: DatePartKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const digitsOnlyValue = event.target.value.replace(/\D/g, "");
      const trimmedValue = digitsOnlyValue.slice(0, PART_MAX_LENGTH[part]);
      const nextState = {
        ...(this.state as DatePickerMultiInput),
        [part]: trimmedValue,
      } as DatePickerMultiInput;

      this.setState(nextState, () => {
        this.updateQuestionValue(nextState);

        if (trimmedValue.length === PART_MAX_LENGTH[part]) {
          this.focusPart(this.getNextPart(part));
        }
      });
    };

  private handleKeyDown =
    (part: DatePartKey) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      if ((event.ctrlKey || event.metaKey) && ["a", "c", "v", "x"].includes(event.key.toLowerCase())) {
        return;
      }

      const navigationKeys = ["Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
      const editKeys = ["Backspace", "Delete"];

      if (navigationKeys.includes(event.key) || editKeys.includes(event.key)) {
        // Let Backspace and Delete act only on this field's content — no cross-field jumps.
        return;
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    };

  private handleFocus = (part: DatePartKey) => () => {
    // Select all on focus so the user can overtype the existing value immediately.
    this.getInputRef(part).current?.select();
  };

  private handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedValue = event.clipboardData.getData("text").trim();
    if (!pastedValue) return;

    const parsedParts =
      parseDateParts(pastedValue) ??
      (() => {
        const digitsOnlyValue = pastedValue.replace(/\D/g, "");
        if (digitsOnlyValue.length !== 8) return null;

        return {
          day:   digitsOnlyValue.slice(0, 2),
          month: digitsOnlyValue.slice(2, 4),
          year:  digitsOnlyValue.slice(4, 8),
        };
      })();

    if (!parsedParts) return;

    event.preventDefault();
    this.setState(parsedParts, () => {
      this.updateQuestionValue(parsedParts);
      this.yearInputRef.current?.focus();
    });
  };

  private handleBlur = (part: DatePartKey) => () => {
    this.normalizePartsOnBlur(part);
  };

  renderElement() {
    const { day, month, year } = this.state as DatePickerMultiInput;
    const isReadOnly = this.question.readOnly || this.isDisplayMode;
    const hasError = this.question.errors.length > 0;
    const inputClassName = `sd-input ${hasError ? "sd-input--error" : ""} ${
      isReadOnly ? "sd-input--readonly" : ""
    }`;

    return (
      <div className="sd-question__content" onPaste={this.handlePaste}>
        <div className="d-flex align-items-end gap-2 gap-md-3 flex-nowrap">
          <div className="d-flex flex-column gap-1" style={{ flex: "1 1 0", minWidth: 0 }}>
            <div className="d-flex flex-wrap gap-1 fs-7 fs-md-5 fs-lg-6 text-gray-700 text-start"><span>Day</span><span>(DD)</span></div>
            <input
              ref={this.dayInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={inputClassName}
              placeholder="DD"
              aria-label="Day"
              maxLength={2}
              value={day}
              onChange={this.handlePartChange("day")}
              onKeyDown={this.handleKeyDown("day")}
              onFocus={this.handleFocus("day")}
              onBlur={this.handleBlur("day")}
              readOnly={isReadOnly}
              style={{ textAlign: "center" }}
            />
          </div>
          <div className="d-flex flex-column gap-1" style={{ flex: "1 1 0", minWidth: 0 }}>
            <div className="d-flex flex-wrap gap-1 fs-7 fs-md-5 fs-lg-6 text-gray-700 text-start"><span>Month</span><span>(MM)</span></div>
            <input
              ref={this.monthInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={inputClassName}
              placeholder="MM"
              aria-label="Month"
              maxLength={2}
              value={month}
              onChange={this.handlePartChange("month")}
              onKeyDown={this.handleKeyDown("month")}
              onFocus={this.handleFocus("month")}
              onBlur={this.handleBlur("month")}
              readOnly={isReadOnly}
              style={{ textAlign: "center" }}
            />
          </div>
          <div className="d-flex flex-column gap-1" style={{ flex: "1.5 1.5 0", minWidth: 0 }}>
            <div className="d-flex flex-wrap gap-1 fs-7 fs-md-5 fs-lg-6 text-gray-700 text-start"><span>Year</span><span>(YYYY)</span></div>
            <input
              ref={this.yearInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={inputClassName}
              placeholder="YYYY"
              aria-label="Year"
              maxLength={4}
              value={year}
              onChange={this.handlePartChange("year")}
              onKeyDown={this.handleKeyDown("year")}
              onFocus={this.handleFocus("year")}
              onBlur={this.handleBlur("year")}
              readOnly={isReadOnly}
              style={{ textAlign: "center" }}
            />
          </div>
        </div>
      </div>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
  return React.createElement(SurveyQuestionDatePickerText, props);
});
